import type { Communication } from 'fhir/r4';
import moment from 'moment';
import { useState, useEffect } from 'react';
import {
  Message,
  Comment,
  Loader,
  Dropdown,
  Button,
  Popup,
  Segment,
  Divider,
  List,
  Icon,
  Dimmer,
} from 'semantic-ui-react';
import type {
  ButtonProps,
  SemanticICONS,
  SemanticCOLORS,
} from 'semantic-ui-react';
import type { Option } from 'types';

import { useAuthContext } from '../../../contexts/AuthContext';
import * as FHIRUtils from '../../../utils/FHIRUtils';
import type { ResourceEntry } from '../../../utils/bundleNarrowing';

/**
 * One Communication note flattened for the connect-notes list, built field by
 * field from the resource's `note` entries and the parsed Communication.
 */
type ConnectNote = {
  // These are written from `Annotation`/`FlatCommunication` fields that are
  // themselves `T | undefined`, and the producer assigns them unconditionally,
  // so under exactOptionalPropertyTypes the union — not `?:` alone — is the
  // truthful declaration.
  author?: string | undefined;
  code: string;
  commId?: string | undefined;
  commNoteIndex: number;
  description?: string | undefined;
  state?: string | undefined;
  text?: string | undefined;
  time?: string | undefined;
};

/**
 * An entry of the "reply to" dropdown listing this patient's Communications.
 *
 * Unlike a plain {@link Option}, `text` is the rendered `List.Item` built by
 * `setDropdown`, not a string.
 */
type CommOption = {
  key: string;
  value: string;
  text: React.ReactNode;
  id: string;
};

function ConnectNotesList(props: {
  practitionerData: unknown;
  notes: ResourceEntry<'Communication'>[] | undefined;
  type: string;
  patientId: string;
  /*
   * The SWR mutator `notes/tabs.tsx` actually passes, bound straight to a
   * semantic-ui `Button`'s `onClick`.
   *
   * SUSPECTED DEFECT (pre-existing): `Button` invokes this with `(event, data)`,
   * so the click event reaches `mutate` as its `data` argument — SWR 0.5.7 reads
   * that as optimistic data and writes a React synthetic event into the
   * `/Communication` cache before revalidating.
   */
  refreshConnectNotes: (...args: never[]) => unknown;
  setConnectNotesResponse: unknown;
  org: string;
}) {
  const authInfo = useAuthContext();
  const _practitionerName = authInfo.practitionerName;
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  // The producer spreads a `ConnectNote[]` into an object literal, so the keys
  // are the array's stringified indices ("0", "1", ...) and the values are notes.
  const [tableData, setTableData] = useState<Record<string, ConnectNote>>({});
  const [allTableData, setAllTableData] = useState<Record<string, ConnectNote>>({});
  const [sortTableData, setSortTableData] = useState(false);
  const [_noteText, _setNoteText] = useState('');
  const [filterNoteTypes, setFilterNoteTypes] = useState<Option[]>([]);

  const [loaderActive, _setLoaderActive] = useState(false);
  const [_open, _setOpen] = useState();

  const [_availableComms, setAvailableComms] = useState({});
  const [_comms, setComms] = useState<CommOption[]>([{ key: '', value: '', text: '', id: '' }]);

  useEffect(() => {
    // reset notes data on new data response
    setTableData({});
    setAllTableData({});
    if (props.notes && props.notes.length) {
      establishParentChildRelationship(props.notes);
    }
  }, [props.notes, props.type]);

  useEffect(() => {
    setValidationErrors([]);
  }, []);
  // Populate validation/error messages on the form
  const setValidationMessages = () => {
    return validationErrors.map((error) => {
      return <Message.Item key={error}>{error}</Message.Item>;
    });
  };
  // SUSPECTED DEFECT (apps/care-betty/src/components/patient/notes/connectNotesList.tsx:96-106)
  // `sortedChildNotes` is populated from `tableData[parentKey].child`, but this
  // component's producer (`establishParentChildRelationship`) builds `ConnectNote`
  // values, which have no `child` property — unlike notes/list.tsx and
  // notes/listAll.tsx, whose producers really do assemble a `child` array.
  // Every entry therefore resolves to `undefined`, and nothing in this file ever
  // reads `sortedChildNotes` afterwards, so the loop is inert today.
  // REPORTED, NOT FIXED: deleting it, or adding `child` to the producer, would
  // both be behaviour changes. Typed as `Record<string, undefined>` to state what
  // it actually holds; the `as` on the read preserves the current unguarded access.
  const sortedChildNotes: Record<string, undefined> = {};

  const sortedParentNotes = Object.keys(tableData).sort(function (a, b) {
    // `.time` is read unguarded and throws on a missing key; the assertion
    // preserves that rather than adding a fallback. `time` is itself optional,
    // and `new Date(undefined)` yields Invalid Date — `getTime()` is NaN, so the
    // comparison keeps the incoming order. The `as string` records that the
    // argument really can be undefined at runtime.
    const timeOf = (note: ConnectNote | undefined) => new Date((note as ConnectNote).time as string).getTime();
    if (!sortTableData) {
      return timeOf(tableData[b]) - timeOf(tableData[a]);
    } else {
      return timeOf(tableData[a]) - timeOf(tableData[b]);
    }
  });

  sortedParentNotes.map(
    (parentKey) => (sortedChildNotes[parentKey] = (tableData[parentKey] as ConnectNote & { child?: undefined }).child)
  );

  const filterNotes = (e: React.MouseEvent<HTMLDivElement>) => {
    // `e.target` is typed `EventTarget`; the handler is bound to a Dropdown.Menu
    // whose click target is always a DOM node, so `textContent` is read off it
    // directly. Asserting to `Node` states that assumption without adding a guard.
    const clickedText = (e.target as Node).textContent;
    const filteredTableData: Record<string, ConnectNote> = {};
    if (clickedText === 'All' || clickedText === '') {
      setTableData(allTableData);
    } else {
      for (const key in allTableData) {
        // `for...in` over a Record still yields `ConnectNote | undefined` under
        // noUncheckedIndexedAccess, but only yields keys that exist.
        const entry = allTableData[key] as ConnectNote;
        if (entry.state === clickedText) {
          filteredTableData[key] = entry;
        }
      }
      setTableData(filteredTableData);
    }
  };

  const sortNotes = (_e: React.MouseEvent<HTMLButtonElement>) => {
    setSortTableData(!sortTableData);
  };
  const displayNoteThread = (key: string, tableData: Record<string, ConnectNote>) => {
    // Every read below dereferences `tableData[key]` unguarded; the assertion
    // preserves that.
    const note = tableData[key] as ConnectNote;
    return (
      <Comment key={key} style={{ marginTop: '10px' }}>
        <Comment.Content>
          <ChannelIcon channel={note.code} state={note.state} />

          <Comment.Author as="a" style={{ cursor: 'default', marginLeft: '7px' }}>
            {/* preserved: the original used optional chaining on this read only */}
            {tableData[key]?.text}
          </Comment.Author>
          <Comment.Metadata>
            {/* <Button compact size="mini" color="red" content="Remove" onClick={() => setOpen(key)} /> */}
          </Comment.Metadata>
          <Comment.Actions style={{ marginLeft: '35px' }}>
            <Comment.Text>
              <div className="noteattr_display_text">
                <span className="note_type_text">{note.description}</span>
                {'/'}
                <span className="note_type_text">{note.state}</span> {note.author && 'added by'}{' '}
                {note.author}. {note.time && moment(note.time).format('MM-DD-YYYY')}
              </div>
            </Comment.Text>
          </Comment.Actions>
        </Comment.Content>
      </Comment>
    );
  };
  const setDropdown = (id: string, state: string | undefined, description: string | undefined) => {
    return (
      <List.Item key={id} as="span">
        <List.Content>
          <List.Description>{description}</List.Description>
          <List.Description>{state ? state.split('|')[0] : state}</List.Description>
        </List.Content>
      </List.Item>
    );
  };

  const establishParentChildRelationship = (notesData: ResourceEntry<'Communication'>[]) => {
    const notesList: ConnectNote[] = [];
    const commsList: CommOption[] = [];
    const commPayloads: Record<string, Communication> = {};
    const filter: Option[] = [{ text: 'All', key: 'ALL', value: 'ALL' }];
    notesData.forEach((comm) => {
      const resource = comm.resource;
      const fhirNote = FHIRUtils.parseCommunication(resource);
      //some relabelling for UI
      if (fhirNote.contactState === 'error') {
        fhirNote.contactState = 'missed';
      }
      if (fhirNote.contactState && fhirNote.id && fhirNote.contactId) {
        commPayloads[fhirNote.id] = resource;
        commsList.push({
          text: setDropdown(fhirNote.id, fhirNote.contactState, fhirNote.topic),
          key: fhirNote.id,
          value: fhirNote.id,
          id: fhirNote.id,
        });
      }

      /* eslint-disable-next-line @typescript-eslint/no-unused-expressions -- short-circuit used as a statement; the rule's only fix is an `if`, which rewrites the expression rather than its type */
      resource.note &&
        resource.note.forEach((commNote, index) => {
          const note: ConnectNote = {
            author: commNote.authorString,
            text: commNote.text,
            time: commNote.time,
            description: fhirNote.topic,
            state: fhirNote.contactState,
            code: isOutbound(resource) ? 'outbound' : 'inbound',
            commId: resource.id,
            commNoteIndex: index,
          };
          notesList.push(note);
          filter.push({ text: fhirNote.contactState, key: fhirNote.contactState, value: fhirNote.contactState });
        });
    });
    // Remove duplciates from Array of objects
    const uniqueFilter = filter.filter((v, i, a) => a.findIndex((v2) => JSON.stringify(v2) === JSON.stringify(v)) === i);
    setFilterNoteTypes(uniqueFilter);

    const sortedNotesList = notesList.sort(function (a, b) {
      return a.time! > b.time! ? 1 : -1;
    });

    setComms(commsList);

    setAvailableComms((prevState) => ({
      ...prevState,
      ...commPayloads,
    }));

    // `{ ...anArray }` yields the array's indices as string keys ("0", "1", …),
    // which is what every consumer here relies on (`Object.keys(tableData)`) —
    // but the spread's static type is the array's, not a Record.
    // `Object.entries` builds the identical object while stating its true type.
    const notesByIndex: Record<string, ConnectNote> = Object.fromEntries(Object.entries(sortedNotesList));

    setTableData((prevState) => ({
      ...prevState,
      ...notesByIndex,
    }));

    setAllTableData((prevState) => ({
      ...prevState,
      ...notesByIndex,
    }));
  };
  return (
    <div>
      <Dimmer active={loaderActive} inverted>
        <Loader></Loader>
      </Dimmer>

      {validationErrors.length > 0 && (
        <Message error>
          <Message.Header>Error:</Message.Header>
          <Message.List>{setValidationMessages()}</Message.List>
        </Message>
      )}

      {/* <Confirm open={open} onCancel={() => setOpen(null)} onConfirm={() => removeNote(open)} /> */}

      <Segment>
        {filterNoteTypes.length > 0 && (
          <>
            <div className="cb_height_30">
              <span className="cb_floatleft">
                <Popup
                  content="Sort"
                  trigger={<Button circular onClick={sortNotes} name="all" icon="sort" color="teal" />}
                />
                <Dropdown icon="filter" floating button className="icon">
                  <Dropdown.Menu>
                    <Dropdown.Header icon="filter" content="Filter by Type" />
                    <Dropdown.Divider />
                    <Dropdown.Menu onClick={(e: React.MouseEvent<HTMLDivElement>) => filterNotes(e)} scrolling>
                      {filterNoteTypes.map((option) => (
                        // Same as in `list.tsx`: React reads `key` off the JSX
                        // attributes before applying the spread, so the explicit one wins.
                        <Dropdown.Item
                          id={option.value}
                          // @ts-expect-error TS2783
                          key={option.value}
                          {...option}
                          style={{ textTransform: 'Capitalize' }}
                        />
                      ))}
                    </Dropdown.Menu>
                  </Dropdown.Menu>
                </Dropdown>
                <Popup
                  content="Refresh Notes"
                  trigger={
                    <Button
                      circular
                      // See the SUSPECTED DEFECT on `refreshConnectNotes`: an SWR
                      // mutator bound to a click handler receives the event as its
                      // optimistic-data argument.
                      onClick={props.refreshConnectNotes as unknown as NonNullable<ButtonProps['onClick']>}
                      name="refresh"
                      icon="refresh"
                      color="orange"
                    />
                  }
                />
              </span>
            </div>
            <Divider />{' '}
          </>
        )}
        <Comment.Group style={{ minHeight: '300px' }}>
          {sortedParentNotes &&
            sortedParentNotes?.length > 0 &&
            sortedParentNotes.map((key) => displayNoteThread(key, tableData))}
        </Comment.Group>
      </Segment>
    </div>
  );
}
//props: channel, state
const ChannelIcon = (props: { channel: string | undefined; state: string | undefined }) => {
  //default inbound call
  let iconName: SemanticICONS = 'phone volume';
  let iconColor: SemanticCOLORS = 'green';
  // `| null`, not `| undefined`: it is seeded `null` and the render below tests
  // `!== null`, so `undefined` would take the two-icon arm where `null` takes the
  // one-icon arm.
  let cornerIcon: SemanticICONS | null = null;
  switch (props.channel) {
    case 'inbound':
      cornerIcon = 'arrow down';
      if (props.state === 'error' || props.state === 'missed') {
        iconColor = 'red';
      }
      if (props.state === 'voicemail') {
        iconColor = 'orange';
      }
      break;
    case 'outbound':
      cornerIcon = 'arrow up';
      iconColor = 'purple';
      if (props.state === 'error') {
        iconColor = 'red';
      }
      break;
    case 'voice':
      /*
       * SUSPECTED DEFECT — REPORTED, NOT FIXED. Both branches test `props.channel`,
       * which this `case` has already fixed at 'voice', so neither can be true and a
       * 'voice'-channel row always renders green. The intent was almost certainly
       * `props.state`, which the sibling cases branch on.
       */
      iconName = 'phone volume';
      if ((props.channel as string) === 'outbound') {
        iconColor = 'orange';
      } else if ((props.channel as string) === 'voicemail') {
        iconColor = 'purple';
      }
      break;
    case 'email':
      iconName = 'mail outline';
      iconColor = 'grey';
      break;
    case 'sms':
      iconName = 'chat';
      iconColor = 'blue';
      break;
    case 'email-survey':
      iconName = 'mail outline';
      iconColor = 'grey';
      break;
    case 'sms-survey':
      iconName = 'chat';
      iconColor = 'blue';
      break;
    default:
      break;
  }
  if (props.state === 'voicemail') {
    iconColor = 'orange';
    cornerIcon = 'mail';
  }
  return cornerIcon !== null ? (
    <>
      <Icon color={iconColor} name={iconName} size="large" />
      <Icon color={iconColor} name={cornerIcon} size="large" />
    </>
  ) : (
    <Icon color={iconColor} name={iconName} size="large" />
  );
};
//some communications may be inbound so check
const isOutbound = (commResource: Communication | undefined): boolean => {
  if (commResource?.received) return false;
  else return true;
};
export default ConnectNotesList;
